import { useState, useEffect, useRef } from 'react';
import { getAllLocations } from '../../services/location';
import useWebData from '../../hooks/useWebData';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faEnvelope, faLocationArrow, faPhone } from '@fortawesome/free-solid-svg-icons';
import { post_CustomerEnquiry } from '../../services/customerEnquiry';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { tenantId } from '../../config/api';
import { Helmet } from 'react-helmet-async';

const ContactUs = () => {
  const hasFetched = useRef(false);
  const { webData }: any = useWebData();
  const [email, setEmail] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [question, setQuestion] = useState<string>("");
  const [visitData, setVisitData] = useState<any[]>([]);
  const [submitted, setSubmitted] = useState<boolean>(false);

  document.title = webData?.companyName + " | Contact Us";

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    getAllLocations(tenantId)
      .then(data => {
        setVisitData(data);
      })
      .catch(error => console.error(error));

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangePhoneNumber = (value: string) => {
    const numericValue = value.replace(/[^0-9]+/g, "");
    const trimmedValue = numericValue.slice(0, 10);

    if (trimmedValue.length <= 3) {
      return setPhone(trimmedValue);
    } else if (trimmedValue.length <= 6) {
      return setPhone(`(${trimmedValue.slice(0, 3)}) ${trimmedValue.slice(3)}`);
    } else if (trimmedValue.length <= 10) {
      return setPhone(`(${trimmedValue.slice(0, 3)}) ${trimmedValue.slice(3, 6)}-${trimmedValue.slice(6)}`);
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    await post_CustomerEnquiry({
      email,
      name,
      question,
      phone,
      created: new Date(),
    }, tenantId)
      .then(() => {
        resetForm();
        setSubmitted(true);
      })
      .catch(error => console.error("Error:", error));
  };

  const resetForm = () => {
    setEmail("");
    setName("");
    setPhone("");
    setQuestion("");
  };

  return (
    <>
      <Helmet>
        <title>Vehicle & Van Rental Support & Locations | Contact Us</title>
        <meta
          name="description"
          content="Find answers to common questions about our vehicle and van rental services, booking process, pricing, policies, and rental requirements."
        />
      </Helmet>
      <div className='mt-[130px]'>
        <section
          style={{ backgroundImage: `url('${webData?.websiteSubPageImage ? webData?.websiteSubPageImage : ""}')` }}
          className="relative contact-us-container flex flex-col justify-center w-full h-[200px] bg-no-repeat bg-cover bg-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" />
          <div className='w-full text-center absolute'>
            <h1 className="text-center text-white text-2xl font-bold">Contact Us</h1>
            <p className="text-center text-white text-sm sm:text-md md:text-xl">We will be happy to assist you with any questions you may have.</p>
          </div>

        </section>


        <div className="p-8 md:py-10 md:px-12 lg:px-20 xl:px-28 2xl:px-36 w-full">
          {submitted && <div className="flex justify-center">
            <div className="text-center md:max-w-xl lg:max-w-3xl">
              <p
                style={{ color: webData?.websiteHeaderColor }}
                className="mb-4 px-6 text-md font-bold text-green-500">Thank you! We'll be in touch soon.</p>
            </div>
          </div>
          }

          <div className="flex flex-wrap">

            <div className="mb-12 w-full shrink-0 grow-0 basis-auto md:px-3 lg:mb-0 lg:w-1/2 lg:px-6">
              <form className="flex flex-col gap-4"
                onSubmit={handleSubmit}>

                <div className="flex flex-col md:flex-row gap-4">
                  <Input
                    type="text"
                    label="Name"
                    pattern=""
                    title=""
                    inputValue={name}
                    setInputValue={setName}
                  />

                  <Input
                    type="email"
                    label="Email Address"
                    pattern=""
                    title=""
                    inputValue={email}
                    setInputValue={setEmail}
                  />
                </div>

                <Input
                  type="text"
                  label="Phone Number"
                  pattern="[\(][0-9]{3}[\)][\s]{1}[0-9]{3}[\-]{1}[0-9]{4}"
                  title="Please Enter a 10 digits numbers."
                  inputValue={phone}
                  setInputValue={onChangePhoneNumber}
                />

                <div className="w-full">
                  <div className="relative w-full">
                    <textarea
                      name="question"
                      className={`peer h-full w-full rounded-[7px] border ${question && "border-t-transparent"} border-gray-200 bg-transparent px-3 py-2.5 font-sans text-sm font-normal text-gray-700 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-gray-200 placeholder-shown:border-t-gray-200 focus:border-2 focus:border-gray-400 focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-gray-50`}
                      rows={6} placeholder=" "
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      required
                    />
                    <label
                      htmlFor="question"
                      style={{ color: webData?.websiteHeaderColor }}
                      className="before:content[' '] after:content[' '] text-green-500 pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none text-[11px] font-normal leading-tight transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-gray-200 before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-gray-200 after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[3.75] peer-placeholder-shown:text-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-gray-500 peer-focus:before:border-t-2 peer-focus:before:border-l-2 peer-focus:before:border-gray-400 peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:border-gray-400 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-gray-500">
                      Question
                    </label>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button
                    btnType="submit"
                    name="Send"
                    height="10"
                    isBorderBtn={false}
                    isProcessing={false}
                    onClickHandler={() => { }}
                  />

                  <Button
                    btnType="button"
                    name="Clear"
                    height="10"
                    isBorderBtn={true}
                    isProcessing={false}
                    onClickHandler={resetForm}
                  />
                </div>

              </form>
            </div>

            <div className="w-full lg:w-1/2 px-4">
              <div className="flex flex-col md:flex-row md:flex-wrap gap-6">
                {/* Call Us */}
                <div className="w-full md:w-1/2">
                  <div className="flex items-start gap-4">
                    <div
                      className="rounded-md bg-blue-50 p-4"
                      style={{ color: webData?.websiteHeaderColor }}
                    >
                      <FontAwesomeIcon icon={faPhone} className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="font-bold mb-1">Call Us</p>
                      <p className="text-sm text-neutral-500">{webData?.websitePhone}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Us */}
                <div className="w-full md:w-1/2">
                  <div className="flex items-start gap-4">
                    <div
                      className="rounded-md bg-blue-50 p-4"
                      style={{ color: webData?.websiteHeaderColor }}
                    >
                      <FontAwesomeIcon icon={faEnvelope} className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="font-bold mb-1">Contact Us</p>
                      <p className="text-sm text-neutral-500">{webData?.websiteEmail}</p>
                    </div>
                  </div>
                </div>

                {/* Visit Us - full width */}
                <div className="w-full">
                  <div className="flex items-start gap-4">
                    <div
                      className="rounded-md bg-blue-50 p-4"
                      style={{ color: webData?.websiteHeaderColor }}
                    >
                      <FontAwesomeIcon icon={faBuilding} className="h-6 w-6" />
                    </div>
                    <div className="flex flex-col w-full">
                      <p className="font-bold mb-2">Visit Us</p>
                      {visitData.map((location, index) => {
                        const fullAddress = `${location?.street}, ${location?.city}, ${location?.state}, ${location?.zipCode}`;
                        const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;

                        return (
                          <div
                            key={index}
                            className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-4 pb-4 border-b"
                          >
                            <div className="flex items-start gap-2 text-sm text-neutral-700 sm:w-3/4">
                              <FontAwesomeIcon
                                icon={faLocationArrow}
                                className="mt-1 text-gray-400"
                                style={{ color: webData?.websiteHeaderColor }}
                              />
                              <div>
                                <p className="font-bold text-neutral-800">
                                  {location?.locationName}
                                </p>
                                <p>{fullAddress}</p>
                              </div>
                            </div>

                            <a
                              href={mapUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 sm:mt-0 px-4 py-2 text-white text-sm font-semibold rounded hover:opacity-90 whitespace-nowrap w-max text-center"
                              style={{ backgroundColor: webData?.websiteHeaderColor }}
                            >
                              <span className="hover:underline decoration-white underline-offset-2">
                                Get Directions
                              </span>
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </>

  );
}

export default ContactUs